import { apiGet, apiPatch, apiPost } from './client.js';

export interface GoogleReviewConfig {
  connected: boolean;
  needsLocation: boolean;
  accountId: string | null;
  locationId: string | null;
  locationName: string | null;
  locationAddress: string | null;
  placeId: string | null;
  reviewLink: string | null;
  /** @deprecated Prefer placeId */
  googlePlaceId: string | null;
  /** @deprecated Prefer reviewLink */
  reviewLinkUrl: string | null;
  reviewRequestEnabled: boolean;
  reviewRequestDelayDays: number;
  reviewRequestMessage: string | null;
  satisfiedMessage: string | null;
  unsatisfiedMessage: string | null;
  /** Days before an unanswered sent review request auto-expires (default 7). */
  reviewRequestReplyExpiryDays?: number;
  npsEnabled: boolean;
  npsDelayDays: number;
  npsSurveyMessage: string | null;
  npsPromoterMessage: string | null;
  npsPassiveMessage: string | null;
  npsDetractorMessage: string | null;
  npsGoogleThreshold: number;
  tokenExpiresAt: string | null;
}

export interface GoogleBusinessLocation {
  accountId: string;
  accountName: string | null;
  locationId: string;
  locationName: string | null;
  address: string | null;
  placeId: string | null;
}

export interface GoogleReviewStats {
  averageRating: number;
  totalReviews: number;
  ratingBreakdown: Record<1 | 2 | 3 | 4 | 5, number>;
  recentReviews: GoogleReview[];
  newReviewsThisWeek: number;
  newReviewsThisMonth: number;
  responseRate: number;
}

export interface GoogleReview {
  reviewId: string;
  reviewer: { displayName: string; profilePhotoUrl?: string | null };
  starRating: 'ONE' | 'TWO' | 'THREE' | 'FOUR' | 'FIVE';
  comment: string;
  createTime: string;
  updateTime: string;
  reviewReply: { comment: string; updateTime: string } | null;
}

export const googleReviewsApi = {
  getConfig() {
    return apiGet<GoogleReviewConfig>('/api/v1/google-reviews/config');
  },

  patchConfig(body: Partial<GoogleReviewConfig>) {
    return apiPatch('/api/v1/google-reviews/config', body);
  },

  saveLocation(body: {
    accountId: string;
    locationId: string;
    locationName?: string;
    placeId?: string | null;
  }) {
    return apiPost('/api/v1/google-reviews/config/location', body);
  },

  listLocations() {
    return apiGet<GoogleBusinessLocation[]>('/api/v1/google-reviews/locations');
  },

  getAuthUrl() {
    return apiGet<{ url: string }>('/api/v1/google-reviews/auth-url');
  },

  disconnect() {
    return apiPost('/api/v1/google-reviews/disconnect');
  },

  getStats() {
    return apiGet<GoogleReviewStats>('/api/v1/google-reviews/stats');
  },

  listReviews(params?: { page?: number; pageSize?: number; responded?: boolean }) {
    const qs = new URLSearchParams();
    if (params?.page) qs.set('page', String(params.page));
    if (params?.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params?.responded !== undefined) qs.set('responded', String(params.responded));
    const q = qs.toString();
    return apiGet<GoogleReview[]>(`/api/v1/google-reviews/reviews${q ? `?${q}` : ''}`);
  },

  postReply(reviewId: string, comment: string) {
    return apiPost(`/api/v1/google-reviews/reviews/${encodeURIComponent(reviewId)}/reply`, {
      comment,
    });
  },

  aiDraft(reviewId: string, body: { reviewText: string; rating: number; storeName?: string }) {
    return apiPost<{ draft: string }>(
      `/api/v1/google-reviews/reviews/${encodeURIComponent(reviewId)}/ai-draft`,
      body,
    );
  },

  testReviewRequest(phone: string) {
    return apiPost('/api/v1/google-reviews/test-review-request', { phone });
  },
};

export const STAR_LABELS: Record<string, number> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
};
